// Utility for translating UI text and caching results
// Now uses Google Translate API for real translations

const CACHE_KEY = 'translations_cache_v1';
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_TRANSLATE_KEY || '';
const MAX_BATCH_SIZE = 128; // Google Translate API limit

function getCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
  } catch {
    return {};
  }
}

function setCache(cache) {
  localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
}

export function getCachedTranslations(lang) {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
    return cache[lang] || {};
  } catch {
    return {};
  }
}

export function setCachedTranslations(lang, translations) {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
    cache[lang] = { ...(cache[lang] || {}), ...translations };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore storage errors
  }
}

// Real Google Translate API call
async function googleTranslateAPI(texts, targetLang) {
  // Defensive: Do not send empty or invalid payloads
  if (!Array.isArray(texts) || texts.length === 0 || !targetLang) {
    console.warn('translate.js: No texts or invalid targetLang for translation', { texts, targetLang });
    return Object.fromEntries((texts || []).map(t => [t, t]));
  }
  const url = `https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_API_KEY}`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      q: texts,
      target: targetLang
    }),
    headers: { 'Content-Type': 'application/json' }
  });
  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error('translate.js: Failed to parse translation API response', err);
    return Object.fromEntries(texts.map(t => [t, t]));
  }
  if (data && data.data && data.data.translations) {
    return Object.fromEntries(texts.map((t, i) => [t, data.data.translations[i]?.translatedText || t]));
  }
  // fallback: return original text if translation fails
  return Object.fromEntries(texts.map(t => [t, t]));
}

export async function translateTexts(texts, targetLang) {
  if (!GOOGLE_API_KEY) {
    const result = {};
    texts.forEach(t => { result[t] = t; });
    return result;
  }
  const cache = getCache();
  const uncached = [];
  const result = {};
  for (const text of texts) {
    if (cache[targetLang] && cache[targetLang][text]) {
      result[text] = cache[targetLang][text];
    } else {
      uncached.push(text);
    }
  }
  // Batch uncached texts to avoid exceeding API limits
  for (let i = 0; i < uncached.length; i += MAX_BATCH_SIZE) {
    const batch = uncached.slice(i, i + MAX_BATCH_SIZE);
    const translations = await googleTranslateAPI(batch, targetLang);
    cache[targetLang] = cache[targetLang] || {};
    for (const text of batch) {
      cache[targetLang][text] = translations[text];
      result[text] = translations[text];
    }
    setCache(cache);
  }
  return result;
} 